var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Flex, Input, InputGroup, Text, InputLeftElement, } from 'shared/components/ui';
import { useFormContext, useWatch, } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import get from 'lodash/get';
import { useFieldContext } from './FieldContext';
import { countries, dialCodeIndexByIso2, Iso2IndexByDialCode } from 'constants/countries';
import { noneEntered } from 'constants/strings';
import { formatPhoneNumber } from 'shared/utils';
import { SingleLineReadOnly } from './SingleLineReadOnly';
import { FormSelect } from './FormSelect';
import { useEffect, useRef } from 'react';
// since I'm not a regex guru https://stackoverflow.com/a/16702965
export var phoneRegex = new RegExp(/^[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/);
export var FormPhoneInput = function (_a) {
    var _b;
    var name = _a.name, countryCodeName = _a.countryCodeName, dialCodeName = _a.dialCodeName, register = _a.register, rules = _a.rules, errors = _a.errors, placeHolder = _a.placeHolder, displayErrors = _a.displayErrors, props = __rest(_a, ["name", "countryCodeName", "dialCodeName", "register", "rules", "errors", "placeHolder", "displayErrors"]);
    // If the name is in a FieldArray, it will be 'fields.index.fieldName' and errors[name] won't return anything, so we are using lodash get
    var errorMessages = get(errors, name);
    var countryCodeErrorMessages = get(errors, countryCodeName);
    var hasError = !!(errors && errorMessages);
    var hasCountryCodeError = !!(errors && countryCodeErrorMessages);
    var countryCode = useWatch({ name: countryCodeName });
    var phoneNumberRules = __assign(__assign(__assign({}, (rules !== null && rules !== void 0 ? rules : {})), { pattern: {
            value: phoneRegex,
            message: 'Please enter a valid phone number.',
        } }), (countryCode
        ? {
            required: 'Please enter a phone number',
        }
        : {
            required: (_b = rules === null || rules === void 0 ? void 0 : rules.required) !== null && _b !== void 0 ? _b : false, // cannot remove register options, must set to false
        }));
    var fieldContext = useFieldContext();
    var formContext = useFormContext();
    if (fieldContext && fieldContext.readOnly && formContext) {
        return (_jsx(PhoneField, { number: formContext.getValues(name), countryCode: formContext.getValues(countryCodeName) }));
    }
    return (_jsxs(Box, __assign({ w: "100%" }, { children: [_jsxs(Flex, __assign({ flexDir: "row" }, { children: [_jsx(PhoneCodeSelect, { "data-testid": countryCodeName, name: countryCodeName, phoneNumberName: name, dialCodeName: dialCodeName, register: register, rules: rules, isInvalid: displayErrors && hasCountryCodeError, mr: 2, w: '120px' }), _jsx(Input, __assign({ placeholder: placeHolder }, (register && register(name, phoneNumberRules)), (displayErrors && hasError && { isInvalid: true }), props))] })), displayErrors && errorMessages && (_jsx(ErrorMessage, { errors: errors, 
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                name: name, render: function (_a) {
                    var message = _a.message;
                    return (_jsx(Box, { children: _jsx(Text, __assign({ "data-testid": "errors-".concat(name), color: "red.500", fontWeight: "semibold" }, { children: message })) }));
                } })), displayErrors && !errorMessages && countryCodeErrorMessages && (_jsx(ErrorMessage, { errors: errors, 
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                name: countryCodeName, render: function (_a) {
                    var message = _a.message;
                    return (_jsx(Box, { children: _jsx(Text, __assign({ "data-testid": "errors-".concat(name), color: "red.500", fontWeight: "semibold" }, { children: message })) }));
                } }))] })));
};
export var PhoneCodeSelect = function (_a) {
    var _b;
    var phoneNumberName = _a.phoneNumberName, dialCodeName = _a.dialCodeName, w = _a.w, mr = _a.mr, rules = _a.rules, formSelectProps = __rest(_a, ["phoneNumberName", "dialCodeName", "w", "mr", "rules"]);
    var setValue = useFormContext().setValue;
    var countryCode = useWatch({ name: formSelectProps.name });
    var phoneNumber = useWatch({ name: phoneNumberName });
    var selectedDialCode = dialCodeIndexByIso2[countryCode];
    var sortedCountries = useRef(countries.sort(function (countryA, countryB) {
        return countryA.iso2 === 'us'
            ? -1
            : countryB.iso2 === 'us'
                ? 1
                : countryA.displayName.localeCompare(countryB.displayName);
    }));
    var options = useRef(sortedCountries.current.map(function (country) { return ({
        label: "".concat(country.displayName, " (+").concat(country.dialCode, ")"),
        value: country.iso2,
    }); })).current;
    useEffect(function () {
        if (!selectedDialCode) {
            setValue(formSelectProps.name, '');
            setValue(dialCodeName, '');
        }
        else {
            setValue(dialCodeName, selectedDialCode);
        }
    }, [selectedDialCode, countryCode]);
    return (_jsxs(InputGroup, __assign({ mr: mr, w: w }, { children: [_jsx(InputLeftElement, { p: 3, pointerEvents: "none", w: "100%", children: _jsx(_Fragment, { children: selectedDialCode && (_jsxs(Flex, __assign({ flexDir: "row", w: "100%" }, { children: [_jsx(Box, { "data-testid": "flag", as: "span", className: "flag-icon flag-icon-".concat(countryCode) }), _jsxs(Text, __assign({ pl: 1, pr: 1, "data-testid": "dialCode" }, { children: ["+", selectedDialCode] }))] }))) }) }), _jsx(FormSelect, __assign({ w: w, options: options, className: selectedDialCode ? 'noContent' : '', placeholder: "Select", rules: __assign(__assign({}, (rules !== null && rules !== void 0 ? rules : {})), { required: {
                        value: !!phoneNumber ||
                            (typeof (rules === null || rules === void 0 ? void 0 : rules.required) === 'object' ? rules.required.value : !!(rules === null || rules === void 0 ? void 0 : rules.required)),
                        message: phoneNumber
                            ? 'Please enter a country code.'
                            : (typeof (rules === null || rules === void 0 ? void 0 : rules.required) === 'object'
                                ? rules.required.message
                                : (_b = rules === null || rules === void 0 ? void 0 : rules.required) === null || _b === void 0 ? void 0 : _b.toString()) || '',
                    } }) }, formSelectProps))] })));
};
export var PhoneField = function (_a) {
    var _b;
    var number = _a.number, countryCodeProp = _a.countryCode, dialCodeProp = _a.dialCode;
    var formatted = formatPhoneNumber(number);
    var digitOnlyDialCode = dialCodeProp === null || dialCodeProp === void 0 ? void 0 : dialCodeProp.replace(/\D/g, ''); // Mixed back of digits only or '+' from API, strip to just digits to be consistent
    var countryCode = (_b = countryCodeProp !== null && countryCodeProp !== void 0 ? countryCodeProp : formatted.countryCode) !== null && _b !== void 0 ? _b : Iso2IndexByDialCode[digitOnlyDialCode !== null && digitOnlyDialCode !== void 0 ? digitOnlyDialCode : '1'];
    var dialCode = digitOnlyDialCode !== null && digitOnlyDialCode !== void 0 ? digitOnlyDialCode : (countryCode
        ? dialCodeIndexByIso2[countryCode]
        : formatted.countryCode
            ? dialCodeIndexByIso2[formatted.countryCode]
            : '1');
    if (!formatted.number) {
        return _jsx(SingleLineReadOnly, { text: noneEntered, noneEntered: true });
    }
    return (_jsxs(Flex, __assign({ "data-testid": "phoneNumberField" }, { children: [dialCode && (_jsxs(_Fragment, { children: [_jsx(Box, { "data-testid": "flag", as: "span", mt: -2, className: "flag-icon flag-icon-".concat(countryCode !== null && countryCode !== void 0 ? countryCode : 'us') }), _jsxs(Text, __assign({ pl: 1, pr: 1, "data-testid": "dialCode" }, { children: ["+", dialCode] }))] })), _jsx(SingleLineReadOnly, { text: formatted.number })] })));
};
